import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { ScrollInput } from "./shared";
import { SET_DEFAULTS, DIFFICULTY, DIFF_COLOR, DIFF_BG } from "../constants";
import { uid, today, calcOrm, buildPool, normalize } from "../helpers";


// ── A "set group": e.g. "3 sets × 5 reps @ 135 lbs" ──────────────────────────
function SetGroup({ group, onChange, onDelete, units = "lbs" }) {
  const displayReps   = group.reps   || group.repsHint;
  const displayWeight = group.weight || group.weightHint;
  const est = calcOrm(displayWeight, displayReps);

  return (
    <div style={{
      background: "var(--bg3)", borderRadius: 10, padding: "10px 12px",
      marginBottom: 8, border: "1px solid var(--border)",
    }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
        {est && <span style={{ fontSize: 11, color: "var(--accent)", fontWeight: 700, marginRight: "auto" }}>~{est} 1RM</span>}
        {!est && <div style={{ flex: 1 }}/>}
        <button onClick={onDelete} style={{ background: "none", border: "none", cursor: "pointer",
          color: "var(--dim)", fontSize: 18, lineHeight: 1 }}>✕</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr auto 1fr", gap: 6, alignItems: "center", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 9, color: "var(--dim)", textAlign: "center", marginBottom: 4,
            textTransform: "uppercase", letterSpacing: "0.08em" }}>Sets</div>
          <ScrollInput
            value={group.sets}
            hint={group.setsHint || SET_DEFAULTS.sets}
            step={1} min={1}
            onChange={v => onChange({ ...group, sets: v })}
            style={{ padding: "10px 4px", fontSize: 16, fontWeight: 400 }}
          />
        </div>

        <span style={{ color: "var(--dim)", fontSize: 14, textAlign: "center", paddingTop: 16 }}>×</span>

        <div>
          <div style={{ fontSize: 9, color: "var(--dim)", textAlign: "center", marginBottom: 4,
            textTransform: "uppercase", letterSpacing: "0.08em" }}>Reps</div>
          <ScrollInput
            value={group.reps}
            hint={group.repsHint || SET_DEFAULTS.reps}
            step={1} min={1}
            onChange={v => onChange({ ...group, reps: v })}
            style={{ padding: "10px 4px", fontSize: 16, fontWeight: 400 }}
          />
        </div>

        <span style={{ color: "var(--dim)", fontSize: 14, textAlign: "center", paddingTop: 16 }}>@</span>

        <div>
          <div style={{ fontSize: 9, color: "var(--dim)", textAlign: "center", marginBottom: 4,
            textTransform: "uppercase", letterSpacing: "0.08em" }}>{units}</div>
          <ScrollInput
            value={group.weight}
            hint={group.weightHint || SET_DEFAULTS.weight}
            step={units === "kg" ? 2.5 : 5} min={0}
            onChange={v => onChange({ ...group, weight: v })}
            style={{ padding: "10px 4px", fontSize: 16, fontWeight: 400 }}
          />
        </div>
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        {DIFFICULTY.map(d => (
          <button key={d} onClick={() => onChange({ ...group, difficulty: d })} style={{
            flex: 1, padding: "6px 4px", borderRadius: 7, fontSize: 11, fontWeight: 700,
            fontFamily: "inherit", cursor: "pointer",
            border: `1px solid ${group.difficulty === d ? DIFF_COLOR[d] : "var(--border)"}`,
            background: group.difficulty === d ? DIFF_BG[d] : "transparent",
            color: group.difficulty === d ? DIFF_COLOR[d] : "var(--dim)",
          }}>{d}</button>
        ))}
      </div>
    </div>
  );
}

function ExerciseBlock({ block, onUpdateGroups, onRemove, units = "lbs" }) {
  const addGroup = () => {
    const last = block.groups[block.groups.length - 1];
    onUpdateGroups([...block.groups, {
      id: uid(),
      sets: "", reps: "", weight: "",
      setsHint:   last?.sets   || last?.setsHint   || SET_DEFAULTS.sets,
      repsHint:   last?.reps   || last?.repsHint   || SET_DEFAULTS.reps,
      weightHint: last?.weight || last?.weightHint || SET_DEFAULTS.weight,
      difficulty: last?.difficulty || "Medium",
    }]);
  };
  const updateGroup = upd => onUpdateGroups(block.groups.map(g => g.id === upd.id ? upd : g));
  const deleteGroup = id => onUpdateGroups(block.groups.filter(g => g.id !== id));

  const totalSets = block.groups.reduce((a, g) => a + (parseInt(g.sets || g.setsHint) || 0), 0);

  return (
    <div style={{ background: "var(--bg2)", borderRadius: 14, padding: "14px 14px 10px",
      marginBottom: 12, border: "1px solid var(--border)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{block.name}</div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
            {(block.muscles || []).slice(0, 3).join(" · ")}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          {totalSets > 0 && <span style={{ fontSize: 11, color: "var(--dim)" }}>{totalSets} sets</span>}
          <button onClick={onRemove} style={{ background: "none", border: "none", cursor: "pointer",
            color: "var(--dim)", fontSize: 20, lineHeight: 1 }}>✕</button>
        </div>
      </div>

      {block.groups.map(g => (
        <SetGroup key={g.id} group={g} units={units}
          onChange={upd => updateGroup(upd)}
          onDelete={() => deleteGroup(g.id)} />
      ))}

      <button onClick={addGroup} style={{
        width: "100%", marginTop: 4, padding: "10px",
        borderRadius: 8, border: "1px dashed var(--border)",
        background: "transparent", color: "var(--muted)", fontSize: 13,
        cursor: "pointer", fontFamily: "inherit",
      }}>+ Add Set Group</button>
    </div>
  );
}

function Picker({ pool, workouts, onAdd, onLoadWorkout, addedIds }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const fn = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const lq = normalize(q);
  const matchedWorkouts = (workouts || []).filter(w => normalize(w.name).includes(lq)).slice(0, 3);
  const matchedExercises = q.length > 1
    ? pool.filter(e => normalize(e.name).includes(lq) && !addedIds.has(e.id)).slice(0, 6)
    : pool.filter(e => !addedIds.has(e.id)).slice(0, 5);

  const hasResults = matchedWorkouts.length > 0 || matchedExercises.length > 0;

  const pickEx = ex => { onAdd(ex); setQ(""); setOpen(false); };
  const pickWk = wk => { onLoadWorkout(wk); setQ(""); setOpen(false); };

  return (
    <div ref={ref} style={{ position: "relative", marginBottom: 14 }}>
      <input
        value={q}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search workouts or exercises…"
        style={{
          width: "100%", boxSizing: "border-box",
          background: "var(--bg3)", border: "1.5px solid var(--accent)",
          borderRadius: 12, padding: "14px 16px", fontSize: 16,
          color: "var(--text)", fontFamily: "inherit", outline: "none",
        }}
      />
      {open && hasResults && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 300,
          background: "var(--bg2)", border: "1px solid var(--border2)",
          borderRadius: 12, overflow: "hidden", boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
          maxHeight: 340, overflowY: "auto",
        }}>
          {matchedWorkouts.length > 0 && (
            <>
              <div style={{ padding: "8px 16px 4px", fontSize: 9, fontWeight: 700, color: "var(--dim)", letterSpacing: "0.12em", textTransform: "uppercase" }}>WORKOUTS</div>
              {matchedWorkouts.map(wk => (
                <div key={wk.id} onClick={() => pickWk(wk)} style={{
                  padding: "12px 16px", cursor: "pointer",
                  borderBottom: "1px solid var(--border)",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--bg3)"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{wk.name}</div>
                    <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                      {wk.exercises?.length || 0} exercises
                    </div>
                  </div>
                  <span style={{ fontSize: 10, background: "rgba(200,255,0,0.1)", padding: "3px 8px",
                    borderRadius: 20, color: "var(--accent)", border: "1px solid rgba(200,255,0,0.2)", flexShrink: 0 }}>
                    workout
                  </span>
                </div>
              ))}
            </>
          )}
          {matchedExercises.length > 0 && (
            <>
              <div style={{ padding: "8px 16px 4px", fontSize: 9, fontWeight: 700, color: "var(--dim)", letterSpacing: "0.12em", textTransform: "uppercase" }}>EXERCISES</div>
              {matchedExercises.map(ex => (
                <div key={ex.id} onClick={() => pickEx(ex)} style={{
                  padding: "12px 16px", cursor: "pointer",
                  borderBottom: "1px solid var(--border)",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--bg3)"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{ex.name}</div>
                    <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                      {(ex.muscles || []).slice(0, 3).join(" · ")}
                    </div>
                  </div>
                  {ex.source === "mine" && (
                    <span style={{ fontSize: 10, background: "var(--bg3)", padding: "3px 8px",
                      borderRadius: 20, color: "var(--muted)", marginLeft: 10, flexShrink: 0 }}>
                      mine
                    </span>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Last session data → silhouette hint groups (values are placeholders, not pre-filled)
function lastSessionGroups(ex) {
  if (!ex?.sets?.length) return [{ id: uid(), sets: "", reps: "", weight: "", setsHint: SET_DEFAULTS.sets, repsHint: SET_DEFAULTS.reps, weightHint: SET_DEFAULTS.weight, difficulty: "Medium" }];
  const lastDate = ex.sets.reduce((max, s) => (s.date > max ? s.date : max), ex.sets[0].date);
  const last = ex.sets.filter(s => s.date === lastDate);
  const order = [], map = new Map();
  for (const s of last) {
    const key = `${s.reps}|${s.weight}|${s.difficulty}`;
    if (map.has(key)) { map.get(key).count++; }
    else {
      const g = {
        id: uid(),
        // All blank — shown as silhouette placeholders
        sets: "", reps: "", weight: "",
        setsHint: "1",
        repsHint: s.reps   ? String(s.reps)   : SET_DEFAULTS.reps,
        weightHint: s.weight ? String(s.weight) : SET_DEFAULTS.weight,
        difficulty: s.difficulty || "Medium",
        count: 1,
      };
      map.set(key, g);
      order.push(g);
    }
  }
  return order.map(g => ({ ...g, setsHint: String(g.count) }));
}

export default function LogSessionModal({ userExercises, userWorkouts = [], workout, onClose, onSave, units = "lbs" }) {
  const pool = buildPool(userExercises);

  const blocksFromWorkout = (wk) => {
    if (!wk?.exercises?.length) return [];
    return wk.exercises.map(exId => {
      const ex = userExercises.find(e => Number(e.id) === Number(exId));
      if (!ex) return null;
      return { id: uid(), poolId: ex.id, name: ex.name, muscles: ex.muscles, groups: lastSessionGroups(ex) };
    }).filter(Boolean);
  };

  const [blocks, setBlocks] = useState(() => blocksFromWorkout(workout));
  const [note, setNote] = useState("");

  useLayoutEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const addedIds = new Set(blocks.map(b => b.poolId));

  const loadWorkout = wk => {
    const newBlocks = blocksFromWorkout(wk);
    // Merge: add exercises not already present
    const existing = new Set(blocks.map(b => b.poolId));
    const toAdd = newBlocks.filter(b => !existing.has(b.poolId));
    setBlocks(prev => [...prev, ...toAdd]);
  };

  const addExercise = ex => {
    const fullEx = userExercises.find(e => Number(e.id) === Number(ex.id))
      || userExercises.find(e => e.name.toLowerCase() === ex.name.toLowerCase());
    const groups = lastSessionGroups(fullEx || null);
    setBlocks(prev => [...prev, { id: uid(), poolId: ex.id, name: ex.name, muscles: ex.muscles, groups }]);
  };

  const removeBlock = id => setBlocks(prev => prev.filter(b => b.id !== id));
  const updateGroups = (blockId, groups) =>
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, groups } : b));

  // Count sets using actual value OR hint (whichever is set)
  const totalSets = blocks.reduce((a, b) =>
    a + b.groups.reduce((c, g) => c + (parseInt(g.sets || g.setsHint) || 0), 0), 0
  );

  const handleFinish = () => {
    const sessionDate = today();
    const updates = blocks.map(block => ({
      name: block.name,
      muscles: block.muscles,
      sets: block.groups
        .filter(g => (g.reps || g.repsHint) && (g.weight || g.weightHint))
        .flatMap(g => {
          const setsCount = Math.max(1, parseInt(g.sets || g.setsHint) || 1);
          const reps      = parseInt(g.reps    || g.repsHint)   || 0;
          const weight    = parseFloat(g.weight || g.weightHint) || 0;
          return Array.from({ length: setsCount }, () => ({
            id: uid(),
            reps,
            weight,
            difficulty: g.difficulty || "Medium",
            date: sessionDate,
          }));
        }),
    })).filter(b => b.sets.length > 0);
    onSave({ date: sessionDate, note, exercises: updates });
    onClose();
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 500,
      background: "var(--bg1, var(--bg))",
      display: "flex", flexDirection: "column",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "max(16px, env(safe-area-inset-top)) 16px 14px",
        borderBottom: "1px solid var(--border)",
        background: "var(--bg2)", flexShrink: 0,
      }}>
        <button onClick={onClose} style={{
          background: "var(--bg3)", border: "1px solid var(--border)",
          borderRadius: 8, cursor: "pointer", color: "var(--muted)",
          fontSize: 13, padding: "8px 14px", fontFamily: "inherit",
        }}>Cancel</button>

        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.06em" }}>{workout ? workout.name : "Log Workout"}</div>
          {totalSets > 0 && <div style={{ fontSize: 11, color: "var(--muted)" }}>{totalSets} sets</div>}
        </div>

        <button onClick={handleFinish} disabled={totalSets === 0} style={{
          background: totalSets > 0 ? "var(--accent)" : "var(--bg3)",
          color: totalSets > 0 ? "#111" : "var(--dim)",
          border: "none", borderRadius: 8, padding: "8px 16px",
          fontSize: 13, fontWeight: 800, cursor: totalSets > 0 ? "pointer" : "default",
          fontFamily: "inherit",
        }}>Finish</button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "14px 14px",
        paddingBottom: "calc(80px + env(safe-area-inset-bottom))" }}>

        <Picker pool={pool} workouts={userWorkouts} onAdd={addExercise} onLoadWorkout={loadWorkout} addedIds={addedIds} />

        {blocks.length === 0 && (
          <div style={{ textAlign: "center", padding: "48px 20px" }}>
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="var(--dim)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 14 }}>
              <circle cx="12" cy="5" r="2"/>
              <path d="M12 7v6M9 9l-3 2M15 9l3 2M9 13l-1 4h8l-1-4"/>
              <rect x="2" y="11" width="3" height="2" rx="1"/><rect x="19" y="11" width="3" height="2" rx="1"/>
              <line x1="5" y1="12" x2="9" y2="12"/><line x1="15" y1="12" x2="19" y2="12"/>
            </svg>
            <div style={{ fontSize: 15, color: "var(--muted)", marginBottom: 6 }}>Search to add exercises</div>
            <div style={{ fontSize: 12, color: "var(--dim)" }}>{workout ? "Or add the exercises from this workout above" : "Bench Press, Squat, Deadlift…"}</div>
          </div>
        )}

        {blocks.map(block => (
          <ExerciseBlock key={block.id} block={block} units={units}
            onUpdateGroups={groups => updateGroups(block.id, groups)}
            onRemove={() => removeBlock(block.id)} />
        ))}

        {blocks.length > 0 && (
          <textarea value={note} onChange={e => setNote(e.target.value)}
            placeholder="Session notes (optional)…"
            style={{
              width: "100%", boxSizing: "border-box",
              background: "var(--bg2)", border: "1px solid var(--border)",
              borderRadius: 10, padding: "12px 14px", fontSize: 14,
              color: "var(--text)", fontFamily: "inherit",
              resize: "none", minHeight: 64, outline: "none", marginTop: 4,
            }}
          />
        )}
      </div>

      {/* Sticky bottom finish */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        padding: "12px 14px",
        paddingBottom: "max(14px, env(safe-area-inset-bottom))",
        background: "var(--bg2)", borderTop: "1px solid var(--border)",
      }}>
        <button onClick={handleFinish} disabled={totalSets === 0} style={{
          width: "100%", padding: "17px",
          borderRadius: 14, border: "none",
          background: totalSets > 0 ? "var(--accent)" : "var(--bg3)",
          color: totalSets > 0 ? "#111" : "var(--dim)",
          fontSize: 16, fontWeight: 800,
          cursor: totalSets > 0 ? "pointer" : "default",
          fontFamily: "inherit",
        }}>
          {totalSets > 0 ? `Finish · ${totalSets} set${totalSets !== 1 ? "s" : ""}` : "Add sets to finish"}
        </button>
      </div>
    </div>
  );
}
